import castArray from 'lodash/castArray';

import { Dictionaries, DictionaryFindOption } from 'chaika-frontend-types/frontend-cms';
import { Locale } from 'chaika-enums';

import { DictionaryApi } from '@api/DictionaryApi';
import { store } from '@store';
import { AnyObject } from '@store/types';
import { getUniqNewChunkedIds } from '@utils/getUniqNewChunkedIds';
import { DictionaryParams } from 'chaika-types/src/dictionary/responses/DictionaryItemViewModel';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

interface DictionaryLoadParams {
    limit?: number;
    locales?: Locale[];
}

class DictionaryService {
    public async load(dictionaryNames: string[], params: DictionaryLoadParams = {}): Promise<void> {
        const { selectedLanguage: locale } = store.authUser;
        const notLoadedDictionaryNames: string[] = [];

        dictionaryNames.forEach(dictionary => {
            if (store.dictionary.getDictionary(dictionary).getSize() === 0) {
                notLoadedDictionaryNames.push(dictionary);
            }
        });

        if (notLoadedDictionaryNames.length) {
            const dictionaries = await Promise.all(
                notLoadedDictionaryNames.map(name =>
                    DictionaryApi.getByNames([name], { locale, limit: params?.limit }),
                ),
            );

            notLoadedDictionaryNames.forEach((dictionaryName, index) => {
                store.dictionary.addDictionaryItems(
                    dictionaryName,
                    (dictionaries[index] as AnyObject)[dictionaryName] || [],
                );
            });
        }
    }

    public async loadDictionaryIfNotExist(dictionaryName: string, limit?: number): Promise<void> {
        const { selectedLanguage: locale } = store.authUser;
        if (store.dictionary.getDictionary(dictionaryName).getSize() === 0) {
            const dictionaries = await DictionaryApi.getByNames([dictionaryName], { locale, limit });

            store.dictionary.addDictionaryItems(dictionaryName, (dictionaries as AnyObject)[dictionaryName]);
        }
    }

    public async loadDictionaryItemsByIds(ids: string | string[]): Promise<void> {
        if (ids.length > 0) {
            const chunkedIds = getUniqNewChunkedIds(store.dictionary.loadedIds, castArray(ids));
            await Promise.all(chunkedIds.map(chunked => this.findAndAdd({ ids: chunked })));
        }
    }

    public async loadTranslatedDictionaryItemsByIds(ids: string | string[]): Promise<void> {
        if (ids.length > 0) {
            const chunkedIds = getUniqChunkedIds(castArray(ids));

            await Promise.all(chunkedIds.map(chunked => this.findAndAddTranslations({ ids: chunked })));
        }
    }

    public async findAndAdd(params: DictionaryFindOption): Promise<Dictionaries> {
        const { selectedLanguage: locale } = store.authUser;
        const dictionaryResponse = await DictionaryApi.find({ locale, limit: 100, ...params });

        (Object.keys(dictionaryResponse) || []).forEach(dictionaryName => {
            store.dictionary.addDictionaryItems(dictionaryName, (dictionaryResponse as AnyObject)[dictionaryName]);
        });

        return dictionaryResponse;
    }

    public async findAndAddTranslations(params: DictionaryFindOption): Promise<Dictionaries> {
        const dictionaryResponse = await DictionaryApi.find({ limit: 100, withTranslations: true, ...params });

        (Object.keys(dictionaryResponse) || []).forEach(dictionaryName => {
            const dictionary = (dictionaryResponse as AnyObject)[dictionaryName] as DictionaryParams[];
            const translationsByLocale = dictionary.reduce<Record<Locale, DictionaryParams[]>>(
                (acc, { titleTranslations, ...item }) => {
                    titleTranslations?.forEach(({ locale, title }) => {
                        if (title) {
                            acc[locale].push({ ...item, title });
                        }
                    });
                    return acc;
                },
                {
                    [Locale.Ru]: [],
                    [Locale.En]: [],
                    [Locale.Ge]: [],
                    [Locale.Am]: [],
                    [Locale.Pt]: [],
                },
            );

            Object.keys(translationsByLocale).forEach(lang => {
                // @ts-ignore
                const translations = translationsByLocale[lang];
                if (translations.length > 0) {
                    store.dictionary.addTranslatedDictionaryItems(
                        lang as Locale,
                        dictionaryName,
                        // @ts-ignore
                        translations,
                    );
                }
            });
        });

        return dictionaryResponse;
    }

    public async updateDictionaries(
        dictionaryNames: string[],
        params: Pick<DictionaryFindOption, 'limit' | 'offset' | 'locale' | 'withTies'>,
    ): Promise<void> {
        const { selectedLanguage: locale } = store.authUser;
        const dictionaries = await DictionaryApi.getByNames(dictionaryNames, { locale, ...params });

        dictionaryNames.forEach(name => {
            if ((dictionaries as AnyObject)[name]) {
                store.dictionary.addDictionaryItems(name, (dictionaries as AnyObject)[name]);
            }
        });
    }
}

export const dictionaryService = new DictionaryService();
